import { parseGenerationFromName } from './workerGeneration';

// VX-4302: worker self-retirement. A committed generation bump is announced on a dedicated NATIVE
// BroadcastChannel (separate from the Recoil sync channel, which the `broadcast-channel` lib wraps
// in an envelope the worker can't read). Each worker generation listens and closes itself when a
// generation strictly greater than its own is committed — making "no zombie worker" a
// worker-guaranteed property rather than one that depends on every client releasing its port. It
// reacts ONLY to the received message (never to persisted state, so a bare-name gen-0 worker under a
// flag-off population never self-closes on a stale value), and it can never INITIATE a bump.

export const GENERATION_COMMIT_CHANNEL = 'pixel-worker-generation-commit';

export interface GenerationCommitMessage {
  generation: number;
}

// VX-4385: the retirement ack. After a retiring generation drains its heavy memory it announces the
// generation it just released on the SAME channel. A freshly-bumped generation waits for this so it
// does not begin decoding (re-growing a fresh OpenJPH WASM heap) while the prior generation's heap is
// still resident — the overlap that doubled committed memory and OOM-crashed the renderers. The shape
// is deliberately distinct from GenerationCommitMessage (no `generation` field) so the retirement
// listener never mistakes an ack for a commit and self-closes a healthy generation.
export interface RetiredGenerationMessage {
  retiredGeneration: number;
}

// Reads the released generation from a channel payload, or null when the payload is a commit or
// malformed. Shared by the worker (so it never acts on its own ack) and the client-side gate.
export function parseRetiredGeneration(data: unknown): number | null {
  const retired = (data as Partial<RetiredGenerationMessage> | null)?.retiredGeneration;
  return typeof retired === 'number' ? retired : null;
}

// A minimal structural channel interface. Deliberately looser than Pick<BroadcastChannel, ...> (whose
// overloaded addEventListener is awkward to satisfy) so both a real `new BroadcastChannel` and a
// plain test stub are assignable without casts.
interface RetirementChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  close(): void;
}
type ChannelFactory = (name: string) => RetirementChannel;

const defaultChannelFactory: ChannelFactory = (name) => new BroadcastChannel(name);

// The commit channel is opened once and kept open for the life of the document. A bump is rare and a
// BroadcastChannel is a trivial resource, so there is no reason to reopen and close it per
// announcement (vs. the old open/post/close dance). Null where BroadcastChannel is unavailable. This
// is the SINGLE main-thread escape-hatch channel instance: every main-thread escape-hatch listener —
// the drain gate's retirement-ack tracking (EscapeHatch/generationDrain) and the coordination hook's
// presence/votes (EscapeHatch/coordination) — subscribes here via `subscribeToCommitChannel` rather
// than opening its own channel. The worker posts acks from its own context, so they arrive here even
// though this instance never receives its own commit posts.
let commitChannel: RetirementChannel | null = null;
export const getCommitChannel = (): RetirementChannel | null => {
  if (commitChannel == null && typeof BroadcastChannel !== 'undefined') {
    commitChannel = defaultChannelFactory(GENERATION_COMMIT_CHANNEL);
  }
  return commitChannel;
};

// VX-4391: fan-out for the single shared main-thread channel instance above. A single native
// `message` listener is registered on the channel exactly once, ever — guarded by
// `dispatcherInstalled` rather than `messageListeners.size === 0` so a later subscribe, after every
// prior subscriber has unsubscribed (e.g. the coordination hook's effect unmounting/remounting),
// reuses the existing dispatcher instead of layering on another native listener that would re-fire
// every live subscriber per stale listener. Dispatches every incoming payload to every subscriber;
// each subscriber narrows `data` to the message shape it cares about (see parseRetiredGeneration and
// coordination's own message parsing) and ignores the rest. This is what lets commit acks and
// coordination presence/votes share one BroadcastChannel instead of each opening a duplicate. No-op
// (returns a no-op unsubscribe) where BroadcastChannel is unavailable, matching the rest of this
// module's degrade-to-no-op behaviour.
const messageListeners = new Set<(data: unknown) => void>();
let dispatcherInstalled = false;
export function subscribeToCommitChannel(listener: (data: unknown) => void): () => void {
  const channel = getCommitChannel();
  if (channel == null) return () => {};
  if (!dispatcherInstalled) {
    dispatcherInstalled = true;
    channel.addEventListener('message', (event) => {
      messageListeners.forEach((subscriber) => subscriber(event.data));
    });
  }
  messageListeners.add(listener);
  return () => messageListeners.delete(listener);
}

// Called at the commit site (an atom effect on the generation state) so every generation bump,
// from any tab, announces itself to the live workers. Pass an explicit channel in tests; no-ops
// where BroadcastChannel is unavailable.
export function broadcastGenerationCommit(
  generation: number,
  channel: RetirementChannel | null = getCommitChannel()
): void {
  channel?.postMessage({ generation } as GenerationCommitMessage);
}

export interface RetirementScope {
  name: string;
  close: () => void;
  // VX-4385: release this generation's heavy memory synchronously — cancel in-flight decodes, dispose
  // connections, and close ports — BEFORE the global is torn down. self.close() alone neither frees
  // the resident decoded buffers nor reclaims the (untearable) OpenJPH WASM heap until the worker is
  // GC'd, so without this the prior generation's footprint lingered while the new one re-grew its own.
  // Optional: transient/legacy scopes that pass a bare `self` keep the old close-only behaviour.
  drain?: () => void;
}

// Installed once at worker boot. Returns an uninstall fn (used in tests). This module is imported
// unconditionally by both SharedWorker entrypoints, so when BroadcastChannel is unavailable it must
// degrade to a no-op rather than throw at boot and break pixel delivery (even with the flag off).
export function installWorkerSelfRetirement(
  scope: RetirementScope,
  createChannel?: ChannelFactory
): () => void {
  const factory =
    createChannel ?? (typeof BroadcastChannel !== 'undefined' ? defaultChannelFactory : undefined);
  if (factory == null) return () => {};
  const ownGeneration = parseGenerationFromName(scope.name);
  const channel = factory(GENERATION_COMMIT_CHANNEL);
  // Retirement is one-way and terminal; guard against a second newer commit re-running drain/close
  // after self.close() has already been scheduled.
  let retired = false;

  const onMessage = (event: MessageEvent) => {
    const committed = (event.data as Partial<GenerationCommitMessage> | null)?.generation;
    // A retired-generation ack has no `generation` field, so it is naturally ignored here — it must
    // never be read as a commit (that would self-close a healthy generation).
    if (typeof committed !== 'number' || committed <= ownGeneration || retired) return;
    retired = true;
    // VX-4385: drain heavy memory first, then announce the released generation so a freshly-bumped
    // generation can wait for this footprint to be gone before it starts decoding, then terminate.
    scope.drain?.();
    channel.postMessage({ retiredGeneration: ownGeneration } as RetiredGenerationMessage);
    scope.close();
  };

  channel.addEventListener('message', onMessage);
  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
}

// VX-4385: structural shapes for the worker-internal teardown, kept dependency-light so this helper
// is unit-testable without the SharedWorker globals and assignable from both worker flavours.
interface RetireableConnection {
  // Method syntax (not a `(options?: ...) => void` property) so the parameter is compared bivariantly:
  // the concrete connections declare dispose() as a method, and strictFunctionTypes' contravariant
  // check on function-typed properties would otherwise add needless assignability friction here. The
  // options are an opaque pass-through (forwarded untouched to the real dispose), hence `unknown`.
  dispose(options?: unknown): void;
}
interface RetireablePortCache {
  // Method syntax for the same bivariance reason as RetireableConnection.dispose, so LRUPortCache
  // (whose forEach/clear are methods) stays structurally assignable. forEach's entries are
  // Nullable<MessagePort> (may be null or undefined), matching LRUPortCache.forEach.
  forEach(fn: (port: Nullable<{ close: () => void }>) => void): void;
  clear(): void;
}

// VX-4385: the teardown a retiring generation runs in its `drain`. Synchronously cancels shared work
// (e.g. the deduped loader's in-flight decodes, via beforeClosePorts), disposes every connection
// (cancelling per-connection work and freeing per-connection loaders), then closes and clears every
// port so the worker reaches the zero-port, GC-eligible state immediately instead of lingering until
// each client independently drops its port. Connections are snapshotted because dispose() mutates the
// live set via its onDispose callback.
export function retireWorkerGeneration({
  connections,
  allPorts,
  beforeClosePorts,
  disposeOptions,
}: {
  connections: Iterable<RetireableConnection>;
  allPorts: RetireablePortCache;
  beforeClosePorts?: () => void;
  // Opaque pass-through forwarded untouched to each connection.dispose(); the helper never inspects it.
  disposeOptions?: unknown;
}): void {
  beforeClosePorts?.();
  for (const connection of [...connections]) {
    connection.dispose(disposeOptions);
  }
  allPorts.forEach((port) => port?.close());
  allPorts.clear();
}
